import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Store, ExternalLink, Ticket, Share2, Clock } from "lucide-react";
import { toast } from "sonner";
import { VoteControl } from "@/components/public/VoteControl";
import { catMeta } from "@/config/categories";
import { trackClick } from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `vor ${Math.max(1, Math.floor(diff / 60))} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  return `vor ${Math.floor(diff / 86400)} Tg.`;
};

export const DealRow = ({ deal, index = 0 }) => {
  const meta = catMeta(deal.category);
  const CatIcon = meta.icon;
  // price_expired kann true/false/"true"/"false" sein – alle Fälle abdecken
  const isExpired = deal.price_expired === true || deal.price_expired === "true";

  const share = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/deal/${deal.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Link kopiert");
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: isExpired ? 0.5 : 1, y: 0 }}
      whileHover={isExpired ? { opacity: 0.85 } : {}}
      transition={{ delay: Math.min(index * 0.04, 0.4) }}
      style={{ filter: isExpired ? "grayscale(100%)" : "none", transition: "filter 0.3s ease" }}
      className={`group relative flex flex-row bg-white rounded-2xl border transition-all duration-300 overflow-hidden h-40 ${
        isExpired
          ? "border-slate-200"
          : "border-slate-200 hover:border-pink-300 hover:shadow-[0_10px_36px_rgba(255,98,165,0.13)] hover:-translate-y-0.5"
      }`}
      data-testid="deal-card-item"
    >
      {/* Vote control — kompakt links */}
      <div className="flex flex-col items-center justify-center gap-1 w-12 shrink-0 border-r border-slate-100 bg-slate-50/60 h-full">
        <VoteControl dealId={deal.id} temperature={deal.temperature} orientation="vertical" />
      </div>

      {/* Image — feste Breite & Höhe, füllt komplett */}
      <Link
        to={`/deal/${deal.id}`}
        className="relative w-44 shrink-0 h-full bg-slate-50 overflow-hidden flex items-center justify-center"
      >
        <img
          src={deal.image_url || "/wizard-icon.png"}
          alt={deal.product_name}
          loading="lazy"
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.05]"
          onError={(e) => { e.target.src = "/wizard-icon.png"; }}
        />
        {isExpired ? (
          <span className="absolute top-2 left-2 bg-slate-500 text-white text-xs font-bold rounded-md px-1.5 py-0.5 shadow-sm">
            Abgelaufen
          </span>
        ) : (
          <span className="absolute top-2 left-2 bg-emerald-500 text-white text-xs font-black rounded-md px-1.5 py-0.5 shadow-sm">
            -{deal.discount_percent}%
          </span>
        )}
      </Link>

      {/* Content */}
      <div className="flex-1 min-w-0 px-4 py-3 flex flex-col justify-between overflow-hidden">
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-xs font-semibold truncate max-w-[140px]" style={{ color: meta.color }}>
              <CatIcon className="h-3 w-3 shrink-0" /> <span className="truncate">{deal.category}</span>
            </span>
            <span className="text-xs text-slate-400 shrink-0"><Clock className="h-3 w-3 inline mr-0.5" />{timeAgo(deal.created_at)}</span>
          </div>

          <Link to={`/deal/${deal.id}`}>
            <h3 className="font-outfit font-bold text-sm sm:text-base leading-snug text-slate-900 group-hover:text-pink-500 transition-colors line-clamp-2">
              {deal.product_name}
            </h3>
          </Link>
        </div>

        <div>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1">
            <span className="font-outfit font-black text-lg text-[#FF3B30]">
              {deal.deal_price.toFixed(2)} €
            </span>
            <span className="text-xs text-slate-400 line-through">{deal.original_price.toFixed(2)} €</span>
            {isExpired && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 font-medium">
                ⚠ Preis evtl. gestiegen
              </span>
            )}
            {!isExpired && deal.shipping_info && (
              <span className={`text-xs rounded px-1.5 py-0.5 font-medium ${
                deal.shipping_info.toLowerCase().includes("prime")
                  ? "text-[#FF9900] bg-[#FF9900]/10"
                  : deal.shipping_info.toLowerCase().includes("kostenlos")
                  ? "text-emerald-600 bg-emerald-50"
                  : "text-slate-500 bg-slate-100"
              }`}>
                {deal.shipping_info.toLowerCase().includes("prime")
                  ? "✓ Prime kostenlos"
                  : deal.shipping_info.toLowerCase().includes("kostenlos")
                  ? "✓ Kostenlose Lieferung"
                  : deal.shipping_info}
              </span>
            )}
            {deal.voucher_code && (
              <span className="text-xs font-semibold text-[#8928E2] bg-[#A259FF]/10 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                <Ticket className="h-3 w-3" /> {deal.voucher_code}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="flex items-center gap-2 text-xs text-slate-400 overflow-hidden">
              <span className="inline-flex items-center gap-1 shrink-0"><Store className="h-3.5 w-3.5" /> {deal.source}</span>
              <Link to={`/deal/${deal.id}`} className="inline-flex items-center gap-1 hover:text-slate-600" data-testid="deal-comment-link">
                <MessageCircle className="h-3.5 w-3.5" /> {deal.comment_count || 0}
              </Link>
              <button onClick={share} className="hover:text-slate-600 transition-colors" data-testid="deal-share" aria-label="Teilen">
                <Share2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <a
              href={deal.affiliate_url || deal.product_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick(deal.id)}
              className="bg-gradient-to-r from-[#A259FF] via-[#FF62A5] to-[#FF8A3D] text-white px-3 py-1.5 rounded-lg font-outfit font-bold text-xs hover:shadow-md hover:shadow-pink-500/25 active:scale-95 transition-all inline-flex items-center gap-1 shrink-0"
              data-testid="zum-deal-cta"
            >
              Zum Deal <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default DealRow;
